import React, { useEffect, useRef, useState } from "react";
import { Mic, Video, Loader2, AlertCircle, CheckCircle2, PhoneCall } from "lucide-react";
import { describeDeviceError } from "@shared/consultation/deviceErrors";
import { DeviceSelect, groupDevices } from "@shared/consultation/DevicePicker";

// Map getUserMedia DOMException → the same shape Daily's camera-error uses.
export function mediaErrorToDeviceError(err, media) {
  switch (err?.name) {
    case "NotAllowedError":
    case "SecurityError":
      return { type: "permissions", blockedBy: "user", blockedMedia: media };
    case "NotFoundError":
    case "DevicesNotFoundError":
    case "OverconstrainedError":
      return { type: "not-found", missingMedia: media };
    case "NotReadableError":
    case "TrackStartError":
    case "AbortError":
      return { type: media.length > 1 ? "cam-mic-in-use" : media[0] === "audio" ? "mic-in-use" : "cam-in-use" };
    case "TypeError":
      return { type: "undefined-mediadevices" };
    default:
      return { type: "unknown" };
  }
}

// Pre-join device check: asks for mic (+camera on video), shows a live level meter / preview,
// and surfaces permission problems before the other participant is waiting on a silent call.
export default function PreCallCheck({ type, lawyer, onReady, onCancel, layout = "fullscreen" }) {
  const video = type === "video";
  const [state, setState] = useState("requesting"); // requesting | ready | error
  const [errorMsg, setErrorMsg] = useState("");
  const [level, setLevel] = useState(0);
  const [heard, setHeard] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [devices, setDevices] = useState({ mics: [], cams: [], speakers: [] });
  const [sel, setSel] = useState({ mic: "", cam: "", speaker: "" }); // user choices
  const [current, setCurrent] = useState({ mic: "", cam: "", speaker: "" }); // what the preview is using
  const [joining, setJoining] = useState(false);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const joiningRef = useRef(false);
  const pickDevice = (kind, id) => setSel((x) => ({ ...x, [kind]: id }));
  const chosen = { mic: sel.mic || current.mic, cam: sel.cam || current.cam, speaker: sel.speaker || current.speaker };

  useEffect(() => {
    let cancelled = false;
    let audioCtx = null;
    let raf = 0;
    if (!streamRef.current) setState("requesting"); // switching devices keeps the ready UI
    const media = video ? ["audio", "video"] : ["audio"];
    (async () => {
      try {
        if (!navigator.mediaDevices?.getUserMedia) throw new TypeError("mediaDevices unavailable");
        streamRef.current?.getTracks().forEach((t) => t.stop());
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: sel.mic ? { deviceId: { exact: sel.mic } } : true,
          video: video ? (sel.cam ? { deviceId: { exact: sel.cam } } : true) : false,
        });
        if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current && video) videoRef.current.srcObject = stream;
        // Labels are only populated after permission — enumerate now and remember the active devices.
        if (navigator.mediaDevices.enumerateDevices) {
          const grouped = groupDevices(await navigator.mediaDevices.enumerateDevices());
          if (!cancelled) {
            setDevices(grouped);
            const micId = stream.getAudioTracks()[0]?.getSettings?.().deviceId || "";
            const camId = stream.getVideoTracks?.()[0]?.getSettings?.().deviceId || "";
            setCurrent({
              mic: micId || grouped.mics[0]?.deviceId || "",
              cam: camId || grouped.cams[0]?.deviceId || "",
              speaker: grouped.speakers[0]?.deviceId || "",
            });
          }
        }
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (Ctx && stream.getAudioTracks().length) {
          audioCtx = new Ctx();
          const src = audioCtx.createMediaStreamSource(stream);
          const analyser = audioCtx.createAnalyser();
          analyser.fftSize = 256;
          src.connect(analyser);
          const buf = new Uint8Array(analyser.frequencyBinCount);
          const tick = () => {
            analyser.getByteFrequencyData(buf);
            const avg = buf.reduce((a, b) => a + b, 0) / buf.length;
            const pct = Math.min(100, Math.round((avg / 128) * 100));
            setLevel(pct);
            if (pct > 8) setHeard(true);
            raf = requestAnimationFrame(tick);
          };
          tick();
        }
        setState("ready");
      } catch (err) {
        if (cancelled) return;
        setErrorMsg(describeDeviceError(mediaErrorToDeviceError(err, media), type));
        setState("error");
      }
    })();
    return () => {
      cancelled = true;
      if (raf) cancelAnimationFrame(raf);
      if (audioCtx) audioCtx.close().catch(() => {});
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [video, type, attempt, sel.mic, sel.cam]);

  const join = () => {
    if (joiningRef.current || state !== "ready") return;
    joiningRef.current = true;
    setJoining(true);
    // Release the preview devices so Daily can claim them.
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    onReady({ mic: chosen.mic, cam: video ? chosen.cam : "", speaker: chosen.speaker });
  };

  const actions = (
    <div className={`flex items-center justify-center gap-3 ${layout === "docked" ? "w-full lg:w-auto" : "mt-6"}`}>
      <button type="button" onClick={onCancel} data-testid="precall-cancel"
        className={`h-11 rounded-full bg-white/10 text-sm font-bold hover:bg-white/20 ${layout === "docked" ? "w-[5.5rem] shrink-0 px-3 lg:w-auto lg:px-5" : "px-5"}`}>
        Cancel
      </button>
      {state === "error" ? (
        <button type="button" onClick={() => setAttempt((a) => a + 1)} data-testid="precall-retry"
          className={`h-11 rounded-full adv-btn-gold font-bold text-sm ${layout === "docked" ? "min-w-0 flex-1 px-3 lg:flex-none lg:px-6" : "px-6"}`}>
          Try again
        </button>
      ) : (
        <button type="button" onClick={join} disabled={state !== "ready" || joining} data-testid="precall-join-btn"
          className={`h-11 rounded-full adv-btn-gold font-bold text-sm inline-flex min-w-0 items-center justify-center gap-2 disabled:opacity-50 ${layout === "docked" ? "flex-1 px-3 lg:flex-none lg:px-6" : "px-6"}`}>
          <PhoneCall size={16} /> {joining ? "Joining…" : `Join ${video ? "video" : "audio"} call`}
        </button>
      )}
    </div>
  );

  return (
    <div className={`${layout === "docked" ? "relative flex w-full min-w-0 flex-1 flex-col overflow-hidden lg:grid lg:place-items-center lg:overflow-y-auto lg:p-6" : "fixed inset-0 z-50 grid place-items-center p-6"} bg-[#0A2540] text-white`} data-testid="precall-check" data-layout={layout}>
      <div className={`${layout === "docked" ? "flex w-full min-w-0 max-w-md flex-1 flex-col min-h-0 lg:flex-none" : "w-full max-w-md text-center"}`}>
        <div className={`${layout === "docked" ? "min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 pt-4 pb-3 text-center lg:flex-none lg:overflow-visible lg:px-0 lg:py-0" : ""}`}>
        <div className="mb-3 rounded-xl border border-[#D4AF37]/30 bg-white/5 px-3 py-2.5 text-left lg:hidden" data-testid="precall-status">
          <p className="text-[10px] font-extrabold tracking-[0.16em] text-[#E8C965]">PRE-CALL CHECK</p>
          <p className="mt-1 text-xs leading-relaxed text-white/90">You're not in the consultation yet. Check your camera and microphone, then join when ready.</p>
          <p className="mt-1 text-[10px] text-white/60 capitalize">{type} consultation · {lawyer?.lawyer_name || "Advoset consultation"}</p>
        </div>
        <h2 className={`${layout === "docked" ? "hidden lg:block" : ""} text-xl font-extrabold`} style={{ fontFamily: "Poppins" }}>
          {video ? "Check your camera & microphone" : "Check your microphone"}
        </h2>
        <p className="mt-1 hidden text-sm text-white/70 lg:block">
          Before we connect you with {lawyer?.lawyer_name || "the other participant"}.
        </p>

        {video && (
          <div className="mt-3 h-[clamp(8rem,23dvh,11rem)] w-full rounded-2xl overflow-hidden bg-black border border-white/10 grid place-items-center lg:mt-5 lg:aspect-video lg:h-auto" data-testid="precall-preview">
            <video ref={videoRef} autoPlay muted playsInline className={`w-full h-full object-cover ${state === "ready" ? "" : "hidden"}`} />
            {state !== "ready" && <Video size={36} className="text-white/30" />}
          </div>
        )}

        {state === "requesting" && (
          <div className="mt-3 flex items-center justify-center gap-2 text-sm text-white/80 lg:mt-6" data-testid="precall-requesting">
            <Loader2 size={18} className="animate-spin text-[#D4AF37]" />
            Allow {video ? "camera and microphone" : "microphone"} access when your browser asks…
          </div>
        )}

        {state === "ready" && (
          <div className="mt-3 text-left rounded-2xl bg-white/5 border border-white/10 p-3 lg:mt-6 lg:p-4" data-testid="precall-mic">
            <div className="flex items-center justify-between text-sm">
              <span className="inline-flex items-center gap-2 font-bold"><Mic size={16} /> Microphone</span>
              <span className={`text-xs font-bold ${heard ? "text-emerald-300" : "text-white/60"}`} data-testid="precall-mic-status">
                {heard ? "Working" : "Say something to test"}
              </span>
            </div>
            <div className="mt-2 h-2 rounded-full bg-white/10 overflow-hidden">
              <div className="h-full bg-emerald-400 transition-[width] duration-75" style={{ width: `${level}%` }} data-testid="precall-mic-level" />
            </div>
            {heard && <p className="mt-2 text-xs text-emerald-200 inline-flex items-center gap-1"><CheckCircle2 size={12} /> Your microphone is picking up sound.</p>}
            {(devices.mics.length > 1 || devices.cams.length > 1 || devices.speakers.length > 1) && (
              <div className="mt-4 pt-3 border-t border-white/10 grid gap-3 sm:grid-cols-2" data-testid="precall-devices">
                <DeviceSelect kind="mic" devices={devices.mics} value={chosen.mic} onChange={(id) => pickDevice("mic", id)} />
                {video && <DeviceSelect kind="cam" devices={devices.cams} value={chosen.cam} onChange={(id) => pickDevice("cam", id)} />}
                <DeviceSelect kind="speaker" devices={devices.speakers} value={chosen.speaker} onChange={(id) => pickDevice("speaker", id)} />
              </div>
            )}
          </div>
        )}

        {state === "error" && (
          <div className="mt-3 text-left rounded-2xl bg-red-500/15 border border-red-400/40 p-3 flex gap-3 lg:mt-6 lg:p-4" data-testid="precall-error">
            <AlertCircle size={18} className="text-red-300 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-bold text-red-100">We can't access your {video ? "camera/microphone" : "microphone"}</p>
              <p className="text-xs text-red-100/80 mt-1">{errorMsg}</p>
            </div>
          </div>
        )}
        </div>
        {layout === "docked" ? (
          <div className="shrink-0 border-t border-white/10 bg-[#0A2540] px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] lg:mt-6 lg:border-0 lg:bg-transparent lg:px-0 lg:py-0">
            {actions}
          </div>
        ) : actions}
      </div>
    </div>
  );
}
